import { ACCOUNT_STATUS, IGetOrganisationPermissions } from "@shared/lib/types";
import { Button, Modal, Row, Table, TableTitleLayout, Tag } from "@shared/ui";
import { useEffect, useState } from "react";

import { ApartmentOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Avatar,
  Badge,
  Col,
  Drawer,
  Flex,
  Input,
  Space
} from "antd";
import { ColumnsType } from "antd/es/table";
import styles from "./styles.module.scss";

import {
  useCreateOrganisation,
  useGetOrganisationFacets,
  useGetOrganisations,
} from "@entities/organisation/lib/hooks";
import { ICreateOrganisationRequest } from "@entities/organisation/lib/types";
import { OrganisationForm } from "@entities/organisation/ui";
import { OrganisationDetails } from "@features/organisation/ui/OrganisationDetails";
import { ToastService } from "@shared/lib/services";
import { IBrandOrganisation } from "@shared/lib/types/brandOrganisation";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import Meta from "antd/es/card/Meta";
import { ArrowRight, Building, Search, Users } from "lucide-react";

export default function OrganisationManagement() {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [accountStatus, setAccountStatus] = useState<ACCOUNT_STATUS[]>([
    ACCOUNT_STATUS.ACTIVE,
  ]);
  const [paegNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string[]>([]);
  const [accountManagers, setAccountManagers] = useState<string[]>([]);
  const [brandType, setBrandType] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [openOrganisationDetails, setOpenOrganisationDetails] = useState<{
    open: boolean;
    organisation: IBrandOrganisation | null;
  }>({
    open: false,
    organisation: null,
  });

  const { organisations, isFetching } = useGetOrganisations(
    pageSize,
    paegNo,
    searchQuery,
    accountStatus,
    brandType,
    accountManagers,
    selectedCompanyName,
    industries
  );

  const { facets } = useGetOrganisationFacets();

  const { create, isCreating, isCreated, createOrganisationError } =
    useCreateOrganisation();

  const [isModalVisible, setModalVisible] = useState(false);

  // const brandTypesToDisplay = facets?.type.map((val) => val._id) || [];
  // const statusToDisplay = facets?.status.map((val) => val._id) || [];
  // const industriesToDisplay = facets?.industries.map((val) => val._id) || [];

  const handleCreateOrganisation = () => {
    setModalVisible(true);
  };

  const handleSubmit = (organisationData: ICreateOrganisationRequest) => {
    create(organisationData);
  };
  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  // const handleStatusChange = (value: string[] | undefined) => {
  //   if (!value) return setAccountStatus([]);
  //   setAccountStatus(value as ACCOUNT_STATUS[]);
  // };
  // const handleCompanyNameChange = (value: string[] | undefined) => {
  //   if (!value) return setSelectedCompanyName([]);
  //   setSelectedCompanyName(value);
  // };
  // const handleAccManagersChange = (value: string[] | undefined) => {
  //   if (!value) return setAccountManagers([]);
  //   setAccountManagers(value);
  // };
  // const handleBrandTypeChange = (value: string[] | undefined) => {
  //   if (!value) return setBrandType([]);
  //   setBrandType(value as BrandType[]);
  // };
  // const handleIndustryChange = (value: string[] | undefined) => {
  //   if (!value) return setIndustries([]);
  //   setIndustries(value);
  // };

  useEffect(() => {
    setPageNo(1);
  }, [
    pageSize,
    searchQuery,
    accountStatus,
    brandType,
    accountManagers,
    selectedCompanyName,
    industries,
  ]);

  const columns: ColumnsType<IBrandOrganisation> = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      render: (_: string, record: IBrandOrganisation) => (
        <Row align="middle" gutter={[8, 8]} style={{ cursor: "pointer" }}>
          <Col>
            <Avatar
              src={record.logoUrl}
              alt="Organisation Logo"
              size={40}
              shape="square"
            />
          </Col>
          <Col>
            <TextWithTooltip text={record.name} limit={30} />
            {/* <span>ID: {record._id}</span> */}
          </Col>
        </Row>
      ),
    },
    // {
    //   title: "Industry",
    //   dataIndex: "industries",
    //   key: "industries",
    //   filterDropdown: (props) => (
    //     <SelectFilter
    //       placeholder="Select Industries"
    //       itemsToDisplay={industriesToDisplay}
    //       selectedItems={industries}
    //       handleSelectionChange={handleIndustryChange}
    //       props={props}
    //     />
    //   ),
    //   render: (industries: string[]) => industries?.join(", "),
    // },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      // filterDropdown: (props) => (
      //   <SelectFilter
      //     placeholder="Select status"
      //     itemsToDisplay={statusToDisplay}
      //     selectedItems={accountStatus}
      //     handleSelectionChange={handleStatusChange}
      //     props={props}
      //   />
      // ),
      render: (status: ACCOUNT_STATUS) => (
        <Tag
          color={status === ACCOUNT_STATUS.ACTIVE ? "#ecfdf5" : "#f9fafb"}
          style={{
            borderRadius: 20,
            color: status === ACCOUNT_STATUS.ACTIVE ? "#047857" : "#374151",
            border:
              status !== ACCOUNT_STATUS.ACTIVE
                ? "1px solid #e5e7eb"
                : "1px solid #d1fae5",
          }}
        >
          <Space>
            <Badge
              status={status === ACCOUNT_STATUS.ACTIVE ? "success" : undefined}
              color={status === ACCOUNT_STATUS.INACTIVE ? "#9ca3af" : ""}
            />
            {status}
          </Space>
        </Tag>
      ),
    },
    {
      title: "Brands",
      dataIndex: "brands",
      key: "brands",
      render: (text, record) => (
        <Space>
          <Avatar
            icon={<Building color="#697386" size={16} />}
            shape="square"
            size={32}
            style={{ backgroundColor: "#f6f9fc" }}
          />
          <span>{record?.brands?.length}</span>
        </Space>
      ),
    },
    {
      title: "Employees",
      dataIndex: "permission",
      key: "permission",
      render: (permission: IGetOrganisationPermissions[]) => {
        const renderedPermissions =
          permission.filter((item) => item.userType === "brand-user") || [];
        return (
          <Space>
            <Avatar
              shape="square"
              icon={<Users color="#697386" size={16} />}
              size={32}
              style={{ backgroundColor: "#f6f9fc" }}
            />
            <span>{renderedPermissions?.length}</span>
          </Space>
        );
      },
    },
    {
      title: "Team",
      key: "team",
      render: (_, record) => {
        const renderedPermissions = record?.permission?.filter(
                      (item) => item.userType !== "brand-user"
                    )
        return (
          <Space>
            <Avatar
              shape="square"
              icon={<Users color="#697386" size={16} />}
              size={32}
              style={{ backgroundColor: "#f6f9fc" }}
            />
            <span>{renderedPermissions?.length}</span>
          </Space>
        )
      } 
    },
    {
      title: "Actions",
      render: (text, record) => (
        <Button icon={<ArrowRight size={18} />} type="text" />
      ),
    },
  ];

  useEffect(() => {
    if (isCreated) {
      setModalVisible(false);
      ToastService.success("Organisation created successfully");
    } else if (createOrganisationError) {
      ToastService.error(createOrganisationError.message);
    }
  }, [isCreated, createOrganisationError]);

  return (
    <Flex vertical gap={20}>
      <Flex justify="space-between" align="center">
        <div>
          <h1 className={styles.title}>
            Organisations
          </h1>
          {/* <Typography.Text>
            Manage all registered companies, their brands, and billing details.
          </Typography.Text> */}
        </div>
      </Flex>
        <Table
          dataSource={organisations?.data}
          columns={columns}
          loading={isFetching}
          rowKey="_id"
          scroll={{
            x: "auto",
          }}
          title={() => <TableTitleLayout 
            filterComponent={
              <Flex style={{width: "100%"}} justify="space-between">
            <Input
            placeholder="Search Companies..."
            prefix={<Search size={16} />}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            style={{ width: 350 }}
          />
          <Button
          icon={<PlusOutlined />}
          type="primary"
          variant="outlined"
          disabled={isCreating}
          onClick={() => handleCreateOrganisation()}
        >
          Add Company
        </Button>
          </Flex>
            }
            totalText={`${organisations?.pagination?.totalCount || 0} records found`}
          />}
          onRow={(record) => ({
            onClick: () => {
              setOpenOrganisationDetails({
                open: true,
                organisation: record,
              });
            },
          })}
          pagination={{
            current: paegNo,
            pageSize: pageSize,
            total: organisations?.pagination?.totalCount || 0,
            onChange: handlePaginationChange,
            showTotal: (total) => (
              <div className={styles.recordLabel}>
                {pageSize >= total
                  ? `Showing 1 to ${total} of ${total} results`
                  : `Showing ${paegNo * pageSize - pageSize + 1} to ${paegNo * pageSize} of ${total} results`}
              </div>
            ),
          }}
        />
      <Modal
        title={
          <div className={styles.formHeader}>
            <div className={styles.headerContent}>
              <div className={styles.iconWrapper}>
                <ApartmentOutlined />
              </div>
              <h2 className={styles.title}>Add New Company</h2>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        centered
        footer={null}
        destroyOnClose
        styles={{
          body: {
            padding: 0,
          },
          content: {
            padding: 0,
          },
          header: {
            margin: 0,
          },
        }}
      >
        <OrganisationForm
          onSubmit={handleSubmit}
          isSubmitting={isCreating}
          initialValues={{}}
        />
      </Modal>
      <Drawer
        open={openOrganisationDetails?.open}
        width={800}
        onClose={() => {
          setOpenOrganisationDetails({
            open: false,
            organisation: null,
          });
        }}
        title={
          <Meta
            style={{
              display: "flex",
              gap: 10,
            }}
            className={styles.meta}
            avatar={
              <Avatar
                src={openOrganisationDetails?.organisation?.logoUrl}
                shape="square"
              />
            }
            title={
              <Flex gap={10} align="center">
                {openOrganisationDetails?.organisation?.name}
                {/* <Button
                  icon={<Pencil size={12} />}
                  type="text"
                  onClick={() => {
                    setOpenEditModal({
                      open: true,
                      organisation: openOrganisationDetails?.organisation,
                    });
                  }}
                /> */}
              </Flex>
            }
            description={
              <Space>
                <Tag
                  color={
                    openOrganisationDetails?.organisation?.status ===
                    ACCOUNT_STATUS.ACTIVE
                      ? "green"
                      : "red"
                  }
                >
                  {openOrganisationDetails?.organisation?.status}
                </Tag>
                ID: {openOrganisationDetails?.organisation?._id}
                {/* {openOrganisationDetails?.organisation?.industries?.join(", ")} */}
              </Space>
            }
          />
        }
        styles={{
          body: {
            padding: 0,
          },
        }}
        className={styles.drawer}
      >
        <OrganisationDetails
          organisationId={openOrganisationDetails?.organisation?._id || ""}
        />
      </Drawer>
    </Flex>
  );
}
